//src/components/MealItem.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import OptionSelector from './OptionSelector';
import TimeSelector from './TimeSelector';
import AddressInput from './AddressInput';
import DeliveryAgreement from './DeliveryAgreement';
import PaymentSelector from './PaymentSelector';
import CutlerySelector from './CutlerySelector';
import ProgressBar from './ProgressBar';
import ErrorMessage from './ErrorMessage';
import { calculateMealPrice } from '../utils/MealCalculations';
import { db } from '../config/firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

const MealItem = ({
  id,
  meal,
  onMealChange,
  onRemoveMeal,
  onDuplicateMeal,
  soups = [],
  soupReplacements = [],
  principles = [],
  proteins = [],
  drinks = [],
  sides = [],
  times = [],
  paymentMethods = [],
  additions = [],
  isIncomplete = false,
  incompleteSlideIndex = null,
  address = {},
  showTutorial,
  setShowTutorial,
  maxMeals,
  totalMeals,
  successMessage,
  setSuccessMessage,
  isTableOrder = false,
  userRole, // Nuevo prop para identificar el rol
}) => {
  const [isExpanded, setIsExpanded] = useState(meal?.__startCollapsed ? false : true);
  const [isAdditionsExpanded, setIsAdditionsExpanded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  // Auto-cierre de sección de Adiciones: usar ref para evitar cierres indeseados
  const additionsCloseRef = useRef(null);
  // Usar refs para coordenadas/timbra de arrastre evita valores "stale"
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const [isSwiping, setIsSwiping] = useState(false); // usado como bandera de arrastre
  const dragDxRef = useRef(0); // desplazamiento actual en px
  const dragStartTimeRef = useRef(0);
  const dragJustHappenedRef = useRef(false); // para bloquear clicks fantasma tras un arrastre
  const [showMaxMealsError, setShowMaxMealsError] = useState(false);
  const slideRef = useRef(null);
  const containerRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const expandedSection = meal.expandedSection !== undefined ? meal.expandedSection : 0;
  const setExpandedSection = (val) => onMealChange(id, 'expandedSection', val);
  // Control de auto-avance para evitar "rebote" cuando el usuario navega manualmente
  const advanceTimeoutRef = useRef(null);

  const clearAdvanceTimeout = () => {
    if (advanceTimeoutRef.current) {
      clearTimeout(advanceTimeoutRef.current);
      advanceTimeoutRef.current = null;
    }
  };

  const scheduleAdvance = () => {
    clearAdvanceTimeout();
    advanceTimeoutRef.current = setTimeout(() => {
      // Asegurar que haya transición al avanzar automáticamente
      if (slideRef.current) {
        slideRef.current.style.transition = 'transform 300ms ease-in-out';
      }
      // Slider advance
      if (currentSlide < slides.length - 1) {
         setCurrentSlide((prev) => prev + 1);
      }

      // Accordion advance (Table Order)
      if (isTableOrder) {
         if (expandedSection !== null && expandedSection < slides.length - 1) {
            setExpandedSection(expandedSection + 1);
         } else if (expandedSection === slides.length - 1) {
            setExpandedSection(null);
         }
      }
      
      advanceTimeoutRef.current = null;
    }, 300);
  };
  
  // Resetear slide a 0 solo cuando se monta o cambia el `id` (nuevo meal creado).
  // Evita que la vista vuelva automáticamente al primer slide mientras el usuario
  // está navegando manualmente entre slides si todavía no seleccionó una opción.
  useEffect(() => {
    const isNewEmptyMeal = !meal?.soup && !meal?.soupReplacement && 
                           (!meal?.principle || meal.principle.length === 0) && 
                           !meal?.protein;
    if (isNewEmptyMeal) {
      setCurrentSlide(0);
    }
  }, [id]);

  // Auto-advance slide when chat widget syncs a selection
  useEffect(() => {
    if (isTableOrder) return;
    const handler = (e) => {
      const targetSlide = e.detail?.slideIndex;
      if (targetSlide !== undefined && targetSlide >= 0) {
        if (slideRef.current) {
          slideRef.current.style.transition = 'transform 300ms ease-in-out';
        }
        setCurrentSlide(targetSlide);
      }
    };
    window.addEventListener('chatSyncAdvanceSlide', handler);
    return () => window.removeEventListener('chatSyncAdvanceSlide', handler);
  }, [isTableOrder]);
  
  useEffect(() => {
    if (!isTableOrder) return;
    const q = query(collection(db, 'tables'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, 
      snap => {
        setTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        window.dispatchEvent(new Event('optionsUpdated'));
      },
      error => {
        console.error('[MealItem] Error loading tables:', error);
        setTables([]);
      }
    );
    return () => unsub();
  }, [isTableOrder]);

  const isWaitress = userRole === 3;

  useEffect(() => {
    if (isWaitress && meal.orderType === undefined) {
      onMealChange(id, 'orderType', null);
    }
  }, [isWaitress, meal.orderType, id, onMealChange]);

  const [pendingTime, setPendingTime] = useState(meal?.time || null);
  const [pendingAddress, setPendingAddress] = useState(meal?.address || {});

  useEffect(() => {
    setPendingTime(meal?.time || null);
    setPendingAddress(meal?.address || {});
  }, [meal]);

  // Sincronizar la proteína seleccionada con los datos actualizados de Firebase
  useEffect(() => {
    if (meal?.protein?.id && proteins.length > 0) {
      const updatedProtein = proteins.find(p => p.id === meal.protein.id);
      if (updatedProtein && updatedProtein.price !== meal.protein.price) {
        // El precio cambió, actualizar la proteína
        onMealChange(id, 'protein', updatedProtein);
      }
    }
  }, [proteins, meal?.protein, id, onMealChange]);

  const isSoupComplete = meal?.soup || meal?.soupReplacement;
  const isPrincipleComplete = meal?.principle && (
    (Array.isArray(meal.principle) && meal.principle.length === 1 && meal.principle[0]?.name === 'Remplazo por Principio' && !!meal?.principleReplacement) ||
    (Array.isArray(meal.principle) && meal.principle.length >= 1 && meal.principle.length <= 2 && !meal.principle.some(opt => opt.name === 'Remplazo por Principio'))
  );

  const isCompleteRice = Array.isArray(meal?.principle) && meal.principle.some(p => ['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(p.name));
  const selectedRiceName = isCompleteRice ? meal.principle.find(p => ['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(p.name))?.name : '';
  const isReplacementWithPrinciple = Array.isArray(meal?.principle) && meal.principle.some(p => p.name === 'Remplazo por Principio') && meal?.principleReplacement;

  const isSidesComplete = isCompleteRice || (Array.isArray(meal?.sides) && meal.sides.length > 0);

  // Limpiar el nombre de la proteína para evitar duplicar el precio
  const cleanProteinName = (proteinName) => {
    if (!proteinName) return 'Selecciona';
    // Remover el precio del nombre si está incluido (formato "+ $X.XXX")
    return proteinName.replace(/\s*\+\s*\$[\d,.]+/g, '').trim();
  };

  const displayMainItem = isCompleteRice ? selectedRiceName : cleanProteinName(meal?.protein?.name) || 'Selecciona';

  const totalSteps = isTableOrder ? (isWaitress ? 6 : 7) : 10; // Incluir bebida también para mesero
  const completedSteps = [
    isSoupComplete,
    isPrincipleComplete,
    isCompleteRice || !!meal?.protein,
    !!meal?.drink,
    isTableOrder ? !!meal?.tableNumber : meal?.cutlery !== null,
    ...(isTableOrder && !isWaitress ? [!!meal?.paymentMethod] : []), // Pago solo para no-mesero en mesa
    ...(isTableOrder ? [] : [!!meal?.time, !!meal?.address?.address, !!meal?.deliveryAgreement, !!meal?.payment]),
    isSidesComplete,
  ].filter(Boolean).length;

  const completionPercentage = Math.min(Math.round((completedSteps / totalSteps) * 100), 100);

  const isComplete = isTableOrder
    ? isWaitress
      ? isSoupComplete && isPrincipleComplete && (isCompleteRice || !!meal?.protein) && !!meal?.drink && !!meal?.tableNumber && isSidesComplete
      : isSoupComplete && isPrincipleComplete && (isCompleteRice || !!meal?.protein) && !!meal?.drink && !!meal?.tableNumber && !!meal?.paymentMethod && isSidesComplete
    : isSoupComplete && isPrincipleComplete && (isCompleteRice || !!meal?.protein) && !!meal?.drink && !!meal?.time && !!meal?.address?.address && !!meal?.deliveryAgreement && !!meal?.payment && meal?.cutlery !== null && isSidesComplete;

  // Auto-collapse completed meals
  useEffect(() => {
    if (isComplete && isExpanded && totalMeals > 1) {
      setTimeout(() => setIsExpanded(false), 1500);
    }
  }, [isComplete, isExpanded, totalMeals]);

  const handleQuickQuantity = (field, option, count) => {
    let value;
    
    if (field === 'principle') {
      value = [option];
    } else if (field === 'sides') {
      // Para sides (múltiple), agregar al array existente si no está ya
      const currentSides = Array.isArray(meal?.sides) ? meal.sides : [];
      const alreadySelected = currentSides.some(s => s.id === option.id);
      value = alreadySelected ? currentSides : [...currentSides, option];
    } else {
      value = option;
    }
    
    handleImmediateChange(field, value);
    if (count > 1) {
      window.dispatchEvent(new CustomEvent('quick-multiply-order', {
        detail: { mealId: id, count, field, value, type: 'lunch' }
      }));
    }
  };



  const handleImmediateChange = (field, value) => {
    let updatedMeal = { ...meal, [field]: value };
    let currentSlideIsComplete = false;

    if (field === 'principle') {
      const isNewSelectionCompleteRice = Array.isArray(value) && value.some(p => ['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(p.name));
      if (isNewSelectionCompleteRice) {
        updatedMeal = { ...updatedMeal, protein: null };
        onMealChange(id, 'protein', null);
      }
      onMealChange(id, 'principle', value);
      currentSlideIsComplete = updatedMeal?.principle && (
        (Array.isArray(updatedMeal.principle) && updatedMeal.principle.length === 1 && updatedMeal.principle[0]?.name === 'Remplazo por Principio' && !!updatedMeal?.principleReplacement) ||
        (Array.isArray(updatedMeal.principle) && updatedMeal.principle.length >= 1 && updatedMeal.principle.length <= 2 && !updatedMeal.principle.some(opt => opt.name === 'Remplazo por Principio'))
      );
      if (isNewSelectionCompleteRice && currentSlide < slides.length - 1) {
        scheduleAdvance();
      }
    } else {
      onMealChange(id, field, value);
    }

    switch (field) {
      case 'soup':
currentSlideIsComplete = !!updatedMeal?.soup && (updatedMeal?.soup.name !== 'Remplazo por Sopa' || !!updatedMeal?.soup.replacement);
        break;
      case 'soupReplacement':
        currentSlideIsComplete = !!updatedMeal?.soupReplacement;
        break;
      case 'principle':
        break;
      case 'protein':
        currentSlideIsComplete = isCompleteRice || !!updatedMeal?.protein;
        break;
      case 'drink':
        currentSlideIsComplete = !!updatedMeal?.drink;
        break;
      case 'cutlery':
        currentSlideIsComplete = updatedMeal?.cutlery !== null;
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      case 'time':
        currentSlideIsComplete = !!updatedMeal?.time;
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      case 'deliveryAgreement':
        currentSlideIsComplete = !!value;
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      case 'payment':
      case 'paymentMethod':
        currentSlideIsComplete = !!updatedMeal?.[field];
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      case 'sides':
        // Check if "Todo incluido" is selected
        // Normalizing match to handle accents
        const hasTodoIncluido = (Array.isArray(value) && value.some(s => {
             const name = (s.name || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
             return name.includes('todo inclu') || s.isTodoIncluido;
        })) || value?.isTodoIncluido; // Check the flag from OptionSelector
        
        if (hasTodoIncluido) {
            console.log('Todo incluido detected, advancing immediately');
            currentSlideIsComplete = true; 
            if (currentSlide < slides.length - 1) scheduleAdvance(); 
        } else {
             // For manual selection, do NOT auto advance immediately.
             currentSlideIsComplete = false; 
        }
        break;
      case 'notes':
      case 'additions':
        currentSlideIsComplete = true;
        if (field === 'additions' && isAdditionsExpanded) {
          if (additionsCloseRef.current) clearTimeout(additionsCloseRef.current);
          additionsCloseRef.current = setTimeout(() => {
            setIsAdditionsExpanded(false);
            additionsCloseRef.current = null;
          }, 10000);
        }
        break;
      case 'tableNumber':
        // Auto-asignar orderType basado en la mesa seleccionada
        const isLlevar = value?.toLowerCase().includes('llevar') || value?.toLowerCase() === 'lllevar';
        updatedMeal.orderType = isLlevar ? 'takeaway' : 'table';
        currentSlideIsComplete = !!updatedMeal?.tableNumber;
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      case 'orderType':
        currentSlideIsComplete = !!updatedMeal?.orderType;
        if (currentSlideIsComplete && currentSlide < slides.length - 1) {
          scheduleAdvance();
        }
        break;
      default:
        break;
    }

    if (currentSlideIsComplete && field !== 'additions' && field !== 'principle' && field !== 'sides' && currentSlide < slides.length - 1) {
      scheduleAdvance();
    }
  };

  const handleOptionConfirm = (field, { selection, replacement }) => {
    if (field === 'principle') {
      let newSelection = selection;
      const isNewSelectionCompleteRice = selection.some(p => ['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(p.name));

      if (isNewSelectionCompleteRice) {
        newSelection = selection.filter(p => ['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(p.name)).slice(-1);
        onMealChange(id, 'protein', null);
      } else if (selection.length > 2) {
        newSelection = selection.slice(0, 2);
      }

      onMealChange(id, 'principle', newSelection);
      if (replacement) onMealChange(id, 'principleReplacement', replacement);

      const updatedMealForCheck = { ...meal, principle: newSelection, principleReplacement: replacement || meal.principleReplacement };
      const checkPrincipleComplete = updatedMealForCheck?.principle && (
        (Array.isArray(updatedMealForCheck.principle) && updatedMealForCheck.principle.length === 1 && updatedMealForCheck.principle[0]?.name === 'Remplazo por Principio' && !!updatedMealForCheck?.principleReplacement) ||
        (Array.isArray(updatedMealForCheck.principle) && updatedMealForCheck.principle.length >= 1 && updatedMealForCheck.principle.length <= 2 && !updatedMealForCheck.principle.some(opt => opt.name === 'Remplazo por Principio'))
      );

      // Only advance if replacement is involved (immediate) OR if triggered by Confirmation (which calls this usually? No, OptionSelector calls this on confirm?)
      // Wait, OptionSelector calls onConfirm. MealItem passes handleOptionConfirm as onConfirm (usually).
      // If we are here, it's confirmed. So we MUST advance.
      if (checkPrincipleComplete && currentSlide < slides.length - 1) {
        scheduleAdvance();
      }
    } else if (field === 'sides') {
      // Confirm sides always advances
       if (currentSlide < slides.length - 1) {
        scheduleAdvance();
      }
    } else {
      onMealChange(id, field, selection);
      if (replacement) onMealChange(id, `${field}Replacement`, replacement);
      if (currentSlide < slides.length - 1) {
        scheduleAdvance();
      }
    }
  };

  const handleTimeConfirm = () => {
    if (pendingTime) {
      handleImmediateChange('time', pendingTime);
    }
  };

  const handleAddressConfirm = (confirmedDetails) => {
    onMealChange(id, 'address', confirmedDetails);
    if (currentSlide < slides.length - 1) scheduleAdvance();
  };

  const filteredSoups = soups.filter(soup => soup.name !== 'Solo bandeja' && soup.name !== 'Remplazo por Sopa');
  const filteredPrinciples = principles.filter(principle =>
    principle.name !== 'Remplazo por Principio' &&
    !['Arroz con pollo', 'Arroz paisa', 'Arroz tres carnes'].includes(principle.name)
  );
  const normalizedAdditions = additions.map(add => ({
    ...add,
    price: add.name === 'Mojarra' ? 8000 : add.price,
    requiresReplacement: add.requiresReplacement || ['Proteína adicional', 'Sopa adicional', 'Principio adicional', 'Bebida adicional'].includes(add.name),
  })).filter(add =>
    add.name !== 'Arroz con pollo' &&
    add.name !== 'Arroz paisa' &&
    add.name !== 'Arroz tres carnes'
  );

  const getReplacementsForAdditions = () => {
    const selectedAdditions = meal?.additions || [];
    const unconfiguredAdditions = selectedAdditions.filter(
      (add) => add.requiresReplacement && (add.name === 'Proteína adicional' ? !add.protein : !add.replacement)
    );
    if (unconfiguredAdditions.length === 0) return [];

    const firstUnconfigured = unconfiguredAdditions[0];
    if (firstUnconfigured.name === 'Sopa adicional') return filteredSoups;
    if (firstUnconfigured.name === 'Principio adicional') return filteredPrinciples;
    if (firstUnconfigured.name === 'Proteína adicional') return proteins.filter((p) => p.name !== 'Mojarra');
    if (firstUnconfigured.name === 'Bebida adicional') return drinks.filter((d) => d.name !== 'Sin bebida');
    return [];
  };

  const shouldShowReplacements = meal?.additions?.some(
    add => (add.name === 'Proteína adicional' && !add.protein) || (add.name === 'Sopa adicional' && !add.replacement) || (add.name === 'Principio adicional' && !add.replacement) || (add.name === 'Bebida adicional' && !add.replacement)
  );

  const slides = [
    ...(isTableOrder
      ? [
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <OptionSelector
                  title="Mesa"
                  emoji="🍽️"
                  options={tables}
                  selected={tables.find(t => t.name === meal?.tableNumber) || null}
                  onImmediateSelect={(option) => handleImmediateChange('tableNumber', option?.name)}
                  onQuickQuantityChange={(option, count) => handleQuickQuantity('tableNumber', option?.name, count)}
                  userRole={userRole}
                />
                {!meal?.tableNumber && (
                  <p className="text-[10px] text-red-600 bg-red-50 p-1 rounded mt-1">
                    Por favor, selecciona una mesa
                  </p>
                )}
              </div>
            ),
            isComplete: !!meal?.tableNumber,
            label: 'Mesa',
            associatedField: 'tableNumber'
          },
          // Ocultar Método de Pago para mesero (userRole === 3)
          ...(userRole === 3 ? [] : [{
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <h4 className="text-sm font-semibold text-green-700 mb-2">Método de Pago</h4>
                <PaymentSelector
                  paymentMethods={paymentMethods}
                  selectedPayment={meal?.paymentMethod}
                  setSelectedPayment={(payment) => handleImmediateChange('paymentMethod', payment)}
                />
                {!meal?.paymentMethod && (
                  <p className="text-sm font-semibold text-red-600 bg-red-50 p-2 rounded mt-2">
                    Por favor, selecciona un método de pago
                  </p>
                )}
              </div>
            ),
            isComplete: !!meal?.paymentMethod,
            label: 'Método de pago',
            associatedField: 'paymentMethod'
          }])
        ]
      : []),
    {
      component: (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
          <OptionSelector
            title="Sopa"
            emoji="🥣"
            options={soups}
            selected={meal?.soup}
            onImmediateSelect={(option) => handleImmediateChange('soup', option)}
            onQuickQuantityChange={(option, count) => handleQuickQuantity('soup', option, count)}
            showReplacements={meal?.soup?.name === 'Remplazo por Sopa'}
            replacements={soupReplacements}
            selectedReplacement={meal?.soupReplacement}
            onImmediateReplacementSelect={(option) => handleImmediateChange('soupReplacement', option)}
            onReplacementQuickQuantityChange={(option, count) => handleQuickQuantity('soupReplacement', option, count)}
            userRole={userRole}
          />
        </div>
      ),
      isComplete: isSoupComplete,
      label: 'Sopa',
      associatedField: 'soup'
    },
    {
      component: (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
          <OptionSelector
            title="Principio"
            emoji="🍚"
            options={principles}
            selected={meal?.principle}
            multiple={true}
            showConfirmButton={!isCompleteRice && !isReplacementWithPrinciple}
            onImmediateSelect={(selection) => handleImmediateChange('principle', selection)}
            onConfirm={(data) => handleOptionConfirm('principle', data)}
            onQuickQuantityChange={(option, count) => handleQuickQuantity('principle', option, count)}
            showReplacements={Array.isArray(meal?.principle) && meal.principle.some(opt => opt.name === 'Remplazo por Principio')}
            replacements={soupReplacements}
            selectedReplacement={meal?.principleReplacement}
            onImmediateReplacementSelect={(option) => handleImmediateChange('principleReplacement', option)}
            onReplacementQuickQuantityChange={(option, count) => handleQuickQuantity('principleReplacement', option, count)}
            userRole={userRole}
          />
        </div>
      ),
      isComplete: isPrincipleComplete,
      label: 'Principio',
      associatedField: 'principle'
    },
    {
      component: (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
          {isCompleteRice ? (
            <div className="text-center">
              <p className="text-sm font-semibold text-green-700">
                {`${selectedRiceName} seleccionado`}
              </p>
              <p className="text-xs text-gray-600">
                La proteína ya está incluida. Si no te gusta, escoge un principio diferente que sí tenga la proteína aparte.
              </p>
              <p className="text-xs text-gray-600">
                Pasa a la siguiente o retrocede, si quieres proteína incluida con el arroz, escoge la proteína como adicional abajo en la ➕ Adiciones para Almuerzo
              </p>
              <div className="mt-2 flex justify-center space-x-4">
                <button
                  onClick={() => setCurrentSlide(currentSlide + 1)}
                  className="px-3 py-1 bg-green-600 text-white rounded-md text-xs hover:bg-green-700"
                >
                  Siguiente
                </button>
                <button
                  onClick={() => setCurrentSlide(currentSlide - 1)}
                  className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-400"
                >
                  Retroceder
                </button>
              </div>
            </div>
          ) : (
            <OptionSelector
              title="Proteína"
              emoji="🍗"
              options={proteins}
              selected={meal?.protein}
              onImmediateSelect={(option) => handleImmediateChange('protein', option)}
              onQuickQuantityChange={(option, count) => handleQuickQuantity('protein', option, count)}
              userRole={userRole}
            />
          )}
        </div>
      ),
      isComplete: isCompleteRice || !!meal?.protein,
      label: 'Proteína',
      associatedField: 'protein'
    },
    // Mostrar Bebida para todos los roles
    ...([{
      component: (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
          <OptionSelector
            title="Bebida"
            emoji="🥤"
            options={drinks}
            selected={meal?.drink}
            onImmediateSelect={(option) => handleImmediateChange('drink', option)}
            onQuickQuantityChange={(option, count) => handleQuickQuantity('drink', option, count)}
            userRole={userRole}
          />
        </div>
      ),
      isComplete: !!meal?.drink,
      label: 'Bebida',
      associatedField: 'drink'
    }]),
    ...(!isTableOrder
      ? [
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <h4 className="text-sm font-semibold text-green-700 mb-2">Cubiertos</h4>
                <CutlerySelector
                  cutlery={meal?.cutlery}
                  setCutlery={(cutlery) => handleImmediateChange('cutlery', cutlery)}
                />
                {meal?.cutlery === null && (
                  <p className="text-[10px] text-red-600 bg-red-50 p-1 rounded mt-1">
                    Por favor, selecciona si necesitas cubiertos
                  </p>
                )}
              </div>
            ),
            isComplete: meal?.cutlery !== null,
            associatedField: 'cutlery'
          },
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <TimeSelector
                  times={times}
                  selectedTime={pendingTime}
                  setSelectedTime={setPendingTime}
                  onConfirm={handleTimeConfirm}
                />
                {!meal?.time && (
                  <p className="text-sm font-semibold text-red-600 bg-red-50 p-2 rounded mt-2">
                    Por favor, selecciona una hora y confirma
                  </p>
                )}
              </div>
            ),
            isComplete: !!meal?.time,
            label: 'Hora',
            associatedField: 'time'
          },
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <p className="mb-2 text-sm text-gray-600 text-center md:text-left">
                  🎉 Ingresa tu dirección y teléfono <strong className="text-green-700">una sola vez</strong>. La próxima vez, solo haz clic en <strong className="text-blue-600">"Confirmar" ¡y listo!</strong>
                </p>
                <AddressInput
                  onConfirm={handleAddressConfirm}
                  onValidityChange={(valid) => setIsAddressValid(valid)}
                  initialAddress={meal?.address || {}}
                />
                {!meal?.address?.address && isAddressValid === false && (
                  <p className="text-[10px] text-red-600 mt-1">
                    Por favor, completa tu dirección y teléfono.
                  </p>
                )}
                {!meal?.address?.address && isAddressValid === true && (
                  <p className="text-[10px] text-gray-600 mt-1">
                    Todo listo. Pulsa <span className="font-semibold">“Confirmar dirección”</span> para guardar.
                  </p>
                )}
              </div>
            ),
            isComplete: !!meal?.address?.address,
            label: 'Dirección',
            associatedField: 'address'
          },
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <DeliveryAgreement
                  accepted={meal?.deliveryAgreement}
                  onAccept={(val) => handleImmediateChange('deliveryAgreement', val)}
                />
              </div>
            ),
            isComplete: !!meal?.deliveryAgreement,
            label: 'Acuerdo',
            associatedField: 'deliveryAgreement'
          },
          {
            component: (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
                <h4 className="text-sm font-semibold text-green-700 mb-2">Método de Pago</h4>
                <PaymentSelector
                  paymentMethods={paymentMethods}
                  selectedPayment={meal?.payment}
                  setSelectedPayment={(payment) => handleImmediateChange('payment', payment)}
                />
                {!meal?.payment && (
                  <p className="text-sm font-semibold text-red-600 bg-red-50 p-2 rounded mt-2">
                    Por favor, selecciona un método de pago
                  </p>
                )}
              </div>
            ),
            isComplete: !!meal?.payment,
            label: 'Método de pago',
            associatedField: 'payment'
          }
        ]
      : []),
    {
      component: (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 shadow-sm slide-item">
          {isCompleteRice ? (
            <div className="text-center">
              <p className="text-sm font-semibold text-green-700">
                {`${selectedRiceName} seleccionado`}
              </p>
              <p className="text-xs text-gray-600">La proteína ya está incluida y viene acompañada de papa a la francesa, ensalada dulce, sopa y bebida.</p>
              <p className="text-xs text-gray-600">Si tienes alguna preferencia, por favor agrégala en las notas adicionales.</p>
              <div className="mt-2">
                <h4 className="text-sm font-semibold text-green-700 mb-1">Notas Adicionales</h4>
                <textarea
                  value={meal?.notes || ''}
                  onChange={(e) => handleImmediateChange('notes', e.target.value)}
                  placeholder="Ejemplo: Sin ensalada dulce, más papa a la francesa, etc"
                  onTouchEnd={(e) => {
                    // Evitar re-enfocar si acabamos de cambiar de slide
                    try { if (window.__lastSlideChange && (Date.now() - window.__lastSlideChange) < 350) return; } catch(_){}
                    e.preventDefault();
                    e.stopPropagation();
                    const el = e.currentTarget;
                    if (el && typeof el.focus === 'function') {
                      try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
                      const len = (el.value || '').length;
                      try { el.setSelectionRange(len, len); } catch(_) {}
                    }
                  }}
                  className="w-full p-2 text-sm border rounded-md"
                  rows="2"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="mb-2 text-sm text-gray-700 px-2">
                Selecciona los acompañamientos que prefieras;{' '}
                <span className="font-semibold">
                  los no seleccionados se considerarán no deseados.
                </span>
              </div>
              <OptionSelector
                title="Acompañamiento"
                emoji="🥗"
                options={sides}
                selected={meal?.sides}
                multiple={true}
                onImmediateSelect={(selection) => handleImmediateChange('sides', selection)}
                onQuickQuantityChange={(option, count) => handleQuickQuantity('sides', option, count)}
                userRole={userRole}
              />
              {!isSidesComplete && (
                <p className="text-xs font-semibold text-red-600 bg-red-50 p-1 rounded mt-1">
                  Por favor, selecciona al menos un acompañamiento
                </p>
              )}
              <div className="mt-2">
                <h4 className="text-sm font-semibold text-green-700 mb-1">Notas Adicionales</h4>
                <textarea
                  value={meal?.notes || ''}
                  onChange={(e) => handleImmediateChange('notes', e.target.value)}
                  placeholder="Ejemplo: Poquito arroz, más plátano en lugar de ensalada, etc"
                  onTouchEnd={(e) => {
                    // Evitar re-enfocar si acabamos de cambiar de slide
                    try { if (window.__lastSlideChange && (Date.now() - window.__lastSlideChange) < 350) return; } catch(_){}
                    e.preventDefault();
                    e.stopPropagation();
                    const el = e.currentTarget;
                    if (el && typeof el.focus === 'function') {
                      try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
                      const len = (el.value || '').length;
                      try { el.setSelectionRange(len, len); } catch(_) {}
                    }
                  }}
                  className="w-full p-2 text-sm border rounded-md"
                  rows="2"
                />
              </div>
            </>
          )}
        </div>
      ),
      isComplete: isSidesComplete,
      label: 'Acompañamiento',
      associatedField: 'sides'
    },
  ];

  // Reset accordion when swapping ID (new slot context)
  // Al montar o cambiar de slot, se mantiene la sección guardada en el estado del objeto

  useEffect(() => {
    if (isIncomplete && incompleteSlideIndex !== null) {
      setIsExpanded(true);
      setCurrentSlide(incompleteSlideIndex);
    }
  }, [isIncomplete, incompleteSlideIndex]);

  useEffect(() => {
    let timer;
    if (isComplete && currentSlide === slides.length - 1) {
      timer = setTimeout(() => {
        if (containerRef.current) containerRef.current.style.height = '0';
        setTimeout(() => setIsExpanded(false), 300);
  }, 10000);
    }
    return () => clearTimeout(timer);
  }, [isComplete, currentSlide, slides.length]);

  // Cuando cambia el slide, desenfocar cualquier input activo y notificar a hijos
  useEffect(() => {
    try {
      if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (_) {}
    try {
      // marcar cambio de slide para que inputs no se re-enfoquen inmediatamente
      try { window.__lastSlideChange = Date.now(); } catch (_) {}
      window.dispatchEvent(new CustomEvent('slideChanged', { detail: { slideIndex: currentSlide } }));
    } catch (_) {}
  }, [currentSlide]);

  // Escuchar evento global slideChanged (por si otro componente lo dispara)
  useEffect(() => {
    const handler = (e) => {
      // Al cambiar de slide, resetear estados temporales para evitar que valores
      // sin confirmar persistan y reabran teclados/editores anteriores.
      try { if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch(_){}
      setPendingTime(meal?.time || null);
      setPendingAddress(meal?.address || {});
    };
    window.addEventListener('slideChanged', handler, { capture: true });
    return () => window.removeEventListener('slideChanged', handler, { capture: true });
  }, [meal]);

  useEffect(() => {
    const handleUpdateSlide = (event) => {
      if (event.detail && event.detail.slideIndex !== undefined) setCurrentSlide(event.detail.slideIndex);
    };

    const mealItem = document.getElementById(`meal-item-${id}`);
    if (mealItem) mealItem.addEventListener('updateSlide', handleUpdateSlide);

    return () => {
      if (mealItem) mealItem.removeEventListener('updateSlide', handleUpdateSlide);
      if (additionsCloseRef.current) clearTimeout(additionsCloseRef.current);
    };
  }, [id]);
  

  useEffect(() => {
    if (!containerRef.current || !slideRef.current || !isExpanded) {
      if (containerRef.current) containerRef.current.style.height = '0';
      return;
    }

    let timeoutId;
    let observedElement = null;

    const updateHeight = () => {
      if (slideRef.current && slideRef.current.children && slideRef.current.children[currentSlide]) {
        const slideHeight = slideRef.current.children[currentSlide].offsetHeight;
        containerRef.current.style.height = `${slideHeight + 8}px`;
      } else {
        containerRef.current.style.height = 'auto';
      }
    };

    const debouncedUpdateHeight = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateHeight, 100);
    };

    debouncedUpdateHeight();

    const observer = new ResizeObserver(() => debouncedUpdateHeight());

    if (slideRef.current && slideRef.current.children[currentSlide]) {
      observedElement = slideRef.current.children[currentSlide];
      observer.observe(observedElement);
    }

    const handleOptionsChange = () => debouncedUpdateHeight();
    window.addEventListener('optionsUpdated', handleOptionsChange);

    return () => {
      if (observedElement) observer.unobserve(observedElement);
      observer.disconnect();
      clearTimeout(timeoutId);
      window.removeEventListener('optionsUpdated', handleOptionsChange);
    };
  }, [currentSlide, isExpanded, soups, soupReplacements, principles, proteins, drinks, sides, times, paymentMethods, additions]);

  const handleNext = () => {
    clearAdvanceTimeout();
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      slideRef.current.style.transform = `translateX(-${(currentSlide + 1) * 100}%)`;
    }
  };

  const handlePrev = () => {
    clearAdvanceTimeout();
    if (currentSlide > 0 && currentSlide < slides.length) {
      setCurrentSlide(currentSlide - 1);
      slideRef.current.style.transform = `translateX(-${(currentSlide - 1) * 100}%)`;
    }
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    setIsSwiping(false);
    dragDxRef.current = 0;
    dragStartTimeRef.current = Date.now();
  // no marcamos arrastre todavía; solo cuando detectemos predominancia horizontal en touchMove
    dragJustHappenedRef.current = false;
    clearAdvanceTimeout(); // si había un auto-avance programado, cancelarlo al comenzar un arrastre
    // Desactivar transición para seguir el dedo (solo si vamos a arrastrar)
    if (slideRef.current) {
      slideRef.current.style.transition = 'none';
    }
    if (containerRef.current) {
      containerRef.current.classList.add('dragging');
    }
  };

  const handleTouchMove = (e) => {
    if (!slideRef.current || !containerRef.current) return;
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const dx = touchX - touchStartXRef.current; // >0 derecha, <0 izquierda
    const dy = touchY - touchStartYRef.current;

    // Solo arrastrar si hay predominancia horizontal
    if (Math.abs(dx) > Math.abs(dy) + 10) {
      e.preventDefault(); // evitar scroll vertical mientras arrastramos horizontalmente
      setIsSwiping(true);
      dragJustHappenedRef.current = true;
      try { window.__isMealDragging = true; } catch {}
      dragDxRef.current = dx;
      const width = containerRef.current.offsetWidth || 1;
      const basePercent = -(currentSlide * 100);
      const dragPercent = (dx / width) * 100;
      const minPercent = -((slides.length - 1) * 100);
      const maxPercent = 0;
      const nextPercent = basePercent + dragPercent;
      const clamped = Math.max(minPercent, Math.min(maxPercent, nextPercent));
      slideRef.current.style.transform = `translateX(${clamped}%)`;
    }
  };

  const finishDrag = useCallback(() => {
    // Solo actuar si hubo arrastre
    if (!isSwiping && Math.abs(dragDxRef.current) < 2) return;
    const width = containerRef.current ? containerRef.current.offsetWidth : 1;
    const dx = dragDxRef.current;
    const dt = Date.now() - dragStartTimeRef.current;
    const velocity = Math.abs(dx) / Math.max(dt, 1); // px/ms

    // Restaurar transición para el snap
    if (slideRef.current) {
      slideRef.current.style.transition = 'transform 250ms ease-out';
    }

    // Criterios para cambiar de slide: desplazamiento suficiente o gesto rápido
    const distanceThreshold = width * 0.22; // 22% del ancho
    const velocityThreshold = 0.6; // px/ms (~600px en 1s)

    let targetIndex = currentSlide;
    if ((dx < -distanceThreshold || (dx < -30 && velocity > velocityThreshold)) && currentSlide < slides.length - 1) {
      targetIndex = currentSlide + 1;
    } else if ((dx > distanceThreshold || (dx > 30 && velocity > velocityThreshold)) && currentSlide > 0) {
      targetIndex = currentSlide - 1;
    }

    setCurrentSlide(targetIndex);
    if (slideRef.current) {
      slideRef.current.style.transform = `translateX(-${targetIndex * 100}%)`;
      // limpiar transición después del snap para no interferir con futuros arrastres
      setTimeout(() => {
        if (slideRef.current) slideRef.current.style.transition = '';
      }, 260);
    }

    // Limpiar estado de arrastre
    touchStartXRef.current = 0;
    touchStartYRef.current = 0;
    dragDxRef.current = 0;
    setIsSwiping(false);
    if (containerRef.current) {
      containerRef.current.classList.remove('dragging');
    }
    // marca global de arrastre reciente para que hijos ignoren touchend inmediato
    try { window.__lastMealDragTime = Date.now(); window.__isMealDragging = false; } catch {}
    // Evitar clicks fantasma inmediatamente después del arrastre
    setTimeout(() => { dragJustHappenedRef.current = false; }, 300);
  }, [isSwiping, currentSlide, slides.length]);

  const handleTouchEnd = () => {
    finishDrag();
  };

  useEffect(() => {
    // Fallback global: si el touch termina fuera del contenedor o el navegador emite touchcancel
    const onGlobalTouchEnd = () => finishDrag();
    const onGlobalTouchCancel = () => finishDrag();
    window.addEventListener('touchend', onGlobalTouchEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', onGlobalTouchCancel, { passive: true, capture: true });
    return () => {
      window.removeEventListener('touchend', onGlobalTouchEnd, { capture: true });
      window.removeEventListener('touchcancel', onGlobalTouchCancel, { capture: true });
    };
  }, [finishDrag]);

  const handleSlideChange = (index) => {
    clearAdvanceTimeout();
    if (slideRef.current) {
      setCurrentSlide(index);
      slideRef.current.style.transform = `translateX(-${index * 100}%)`;
    }
  };

  const handleAddAddition = (addition) => {
    const existingAddition = meal?.additions?.find(a => a.id === addition.id);
    const updatedAdditions = meal?.additions ? [...meal.additions] : [];
    if (existingAddition) {
      updatedAdditions[updatedAdditions.findIndex(a => a.id === addition.id)] = {
        ...existingAddition,
        quantity: (existingAddition.quantity || 1) + 1,
      };
    } else {
      updatedAdditions.push({ ...addition, quantity: 1 });
    }
    handleImmediateChange('additions', updatedAdditions);
  };

  const handleRemoveAddition = (additionId) => {
    const updatedAdditions = (meal?.additions || [])
      .map(add => add.id === additionId ? { ...add, quantity: (add.quantity || 1) - 1 } : add)
      .filter(add => add.quantity > 0);
    handleImmediateChange('additions', updatedAdditions);
  };

  const handleIncreaseAddition = (additionId) => {
    const updatedAdditions = (meal?.additions || []).map(add =>
      add.id === additionId ? { ...add, quantity: (add.quantity || 1) + 1 } : add
    );
    handleImmediateChange('additions', updatedAdditions);
  };

  const handleDuplicateClick = () => {
    if (totalMeals >= maxMeals) {
      setShowMaxMealsError(true);
      setTimeout(() => setShowMaxMealsError(false), 3000);
      return;
    }
    setShowMaxMealsError(false);
    onDuplicateMeal(meal);
  };

  return (
    <div id={`meal-item-${id}`} className="relative mb-2">
      <div className="relative bg-white rounded-lg shadow-md">
        <div
          className="sticky top-0 z-[10000] bg-white p-2 border-b border-gray-200 rounded-t-lg"
          onClick={() => {
            if (!isExpanded) setIsExpanded(true);
            else {
              if (containerRef.current) {
                containerRef.current.style.height = '0';
                setTimeout(() => setIsExpanded(false), 300);
              } else {
                setIsExpanded(false);
              }
            }
          }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center cursor-pointer hover:bg-gray-50">
            <div className="flex items-center mb-1 sm:mb-0">
              <div className={`w-6 h-6 rounded-full mr-2 flex items-center justify-center ${isComplete ? 'bg-green-700 text-white' : 'bg-green-200 text-green-700'} text-xs font-medium`}>
                {isComplete ? '✓' : id + 1}
              </div>
              <div>
                <h3 className="font-bold text-sm text-gray-800">
                  Almuerzo #{id + 1} - {displayMainItem} - ${calculateMealPrice(meal, userRole).toLocaleString('es-CO')}
                </h3>
                <ProgressBar progress={completionPercentage} className="w-24 sm:w-32 mt-1" />
              </div>
            </div>
            <div className="flex items-center space-x-1 mt-1 sm:mt-0">
              {isComplete && (
                <span className="hidden sm:inline-flex">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-200 text-green-700">Completo</span>
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateClick();
                }}
                className={`duplicate-button p-1 text-green-700 hover:text-green-800 flex items-center transition-colors ${totalMeals >= maxMeals ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-label={`Duplicar Almuerzo #${id + 1}`}
                disabled={totalMeals >= maxMeals}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-bold">Duplicar</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveMeal(id);
                }}
                className="remove-button p-1 text-red-600 hover:text-red-700 flex items-center transition-colors"
                aria-label={`Eliminar Almuerzo #${id + 1}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-bold">Eliminar</span>
              </button>
            </div>
          </div>
        </div>

        {showMaxMealsError && (
          <ErrorMessage
            message="Has alcanzado el máximo de 15 almuerzos. No puedes duplicar más."
            className="fixed top-4 right-4 z-50 bg-green-100 text-green-800"
            onClose={() => setShowMaxMealsError(false)}
          />
        )}
        {successMessage && !showMaxMealsError && (
          <ErrorMessage
            message={successMessage}
            className="fixed top-12 right-4 z-50 bg-green-100 text-green-800 mt-2"
            onClose={() => setSuccessMessage('')}
          />
        )}
        {isExpanded && (
          <div className="p-2">
            {isTableOrder ? (
              <div className="flex flex-col space-y-2">
                {slides.map((slide, index) => (
                  <div key={index} className="border rounded-md bg-white shadow-sm overflow-hidden">
                    <button
                      onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                      className={`w-full p-3 flex justify-between items-center text-left text-sm font-bold transition-colors ${
                        slide.isComplete 
                          ? 'bg-green-50 text-green-800' 
                          : expandedSection === index 
                            ? 'bg-blue-50 text-blue-800' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        {slide.isComplete && <span className="mr-2 text-green-600 font-bold">✓</span>}
                        <span>{slide.label}</span>
                      </div>
                      <span className="text-gray-400">
                        {expandedSection === index ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </span>
                    </button>
                    {expandedSection === index && (
                      <div className="p-2 border-t border-gray-100 bg-white">
                        {slide.component}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
            <>
            <div
              ref={containerRef}
              className="relative overflow-hidden rounded-lg"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClickCapture={(e) => {
                // Si hubo arrastre reciente, bloquear click para evitar selecciones accidentales
                if (dragJustHappenedRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              style={{ transition: 'height 0.3s ease-in-out', touchAction: 'pan-y' }}
            >
              <div
                ref={slideRef}
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {slides.map((slide, index) => (
                  <div key={index} className="w-full flex-shrink-0" style={{ height: 'fit-content' }}>
                    <div className="p-2" style={{ height: 'fit-content' }}>{slide.component}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center mt-1">
              <button
                className="prev-button p-1 rounded-full text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePrev}
                disabled={currentSlide === 0}
                aria-label="Anterior"
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="flex space-x-1">
                {slides.map((slide, index) => (
                  <button
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${currentSlide === index ? 'bg-green-700' : slide.isComplete ? 'bg-green-400' : 'bg-green-200'}`}
                    aria-label={`Ir a ${slide.label}`}
                    title={slide.label}
                  />
                ))}
              </div>
              <button
                className="next-button p-1 rounded-full text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={currentSlide === slides.length - 1}
                aria-label="Siguiente"
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            </>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md mt-2 p-3">
        <div
          className="flex items-center cursor-pointer justify-between p-2 hover:bg-gray-50"
          onClick={() => {
            setIsAdditionsExpanded(!isAdditionsExpanded);
            if (additionsCloseRef.current) {
              clearTimeout(additionsCloseRef.current);
              additionsCloseRef.current = null;
            }
          }}
        >
          <h3 className="text-sm font-medium text-gray-700 flex flex-wrap items-center gap-x-1">
            <span className="text-base">➕</span>
            <span>Adiciones para Almuerzo #{id + 1}</span>
            <span className="font-bold text-gray-800">(opcional)</span>
          </h3>
          <span className="ml-auto text-xs text-gray-500">
            {isAdditionsExpanded ? 'Ocultar' : 'Mostrar'}
          </span>
        </div>
        {isAdditionsExpanded && (
          <div className="mt-2" onMouseEnter={() => { if (additionsCloseRef.current) { clearTimeout(additionsCloseRef.current); additionsCloseRef.current = null; } }} onTouchStart={() => { if (additionsCloseRef.current) { clearTimeout(additionsCloseRef.current); additionsCloseRef.current = null; } }}>
            <OptionSelector
              title="Adiciones (por almuerzo)"
              emoji="➕"
              options={normalizedAdditions}
              selected={meal?.additions || []}
              multiple={true}
              showReplacements={shouldShowReplacements}
              replacements={getReplacementsForAdditions()}
              userRole={userRole}
              onImmediateSelect={(selection) => {
                const updatedSelection = selection.map(add => {
                  const existingAdd = meal?.additions?.find(a => a.id === add.id);
                  return {
                    ...add,
                    quantity: existingAdd ? (existingAdd.quantity || 1) : 1,
                    protein: add.name === 'Proteína adicional' ? (add.protein || '') : add.protein || '',
                    replacement: (add.name === 'Sopa adicional' || add.name === 'Principio adicional') ? (add.replacement || '') : add.replacement || '',
                  };
                });
                handleImmediateChange('additions', updatedSelection);
              }}
              onImmediateReplacementSelect={({ id: additionId, replacement }) => {
                const updatedAdditions = (meal?.additions || []).map((add) => {
                  if (add.id === additionId) {
                    return {
                      ...add,
                      protein: add.name === 'Proteína adicional' ? replacement?.name || add.protein : add.protein,
                      replacement:
                        ['Sopa adicional', 'Principio adicional', 'Bebida adicional'].includes(add.name)
                          ? replacement?.name || add.replacement
                          : add.replacement,
                    };
                  }
                  return add;
                });
                handleImmediateChange('additions', updatedAdditions);
              }}
              onAdd={handleAddAddition}
              onRemove={handleRemoveAddition}
              onIncrease={handleIncreaseAddition}
            />
            {meal?.additions?.length > 0 && (
              <div className="mt-2 text-sm font-semibold text-gray-700">
                Total Adiciones de este almuerzo: $
                {meal.additions.reduce((sum, item) => sum + (item?.price || 0) * (item?.quantity || 1), 0).toLocaleString('es-CO')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MealItem;